// Tournament Hub (v3 teardown).
//
// Lists active international + UEFA tournaments and renders fixtures,
// standings, winner odds and the bracket under flat hairline chrome.

import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Trophy, Calendar, Users, Globe } from "lucide-react";
import { API } from "../App";
import TopNav from "../components/TopNav";
import {
  V3Page, V3Container, V3Header, V3Eyebrow, V3Section, V3Label,
  V3Tag, V3Num,
} from "../components/v3";

const TournamentHubPage = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState([]);
  const [loadingActive, setLoadingActive] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    setLoadingActive(true);
    axios
      .get(`${API}/tournaments/active`, { timeout: 30000 })
      .then((r) => {
        const list = r.data?.active || [];
        setActive(list);
        if (list.length > 0) setSelected(list[0].slug);
      })
      .catch(() => setActive([]))
      .finally(() => setLoadingActive(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoadingDetail(true);
    axios
      .get(`${API}/tournaments/${selected}`, { timeout: 30000 })
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(null))
      .finally(() => setLoadingDetail(false));
  }, [selected]);

  return (
    <V3Page testid="tournament-hub-page">
      <TopNav />
      <V3Container>
        <V3Header
          testid="tournament-hub-header"
          eyebrow={<V3Eyebrow icon={Trophy}>Tournament hub</V3Eyebrow>}
          title="Tournament hub."
          titleAccent="Standings · brackets · winner odds."
          subtitle="Auto-active during World Cup, AFCON, Euro, Nations League and the UEFA club competitions. Live standings, model-implied winner odds and all upcoming fixtures in one place."
        />

        {loadingActive ? (
          <div
            className="py-16 text-[13px]"
            style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
          >
            Loading active tournaments…
          </div>
        ) : active.length === 0 ? (
          <V3Section testid="tournament-empty">
            <div
              className="p-10 text-center"
              style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}
            >
              <Globe className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--v3-text-4)" }} />
              <p className="text-base" style={{ fontWeight: 500 }}>No tournaments running right now</p>
              <p className="mt-2 text-[13px] max-w-md mx-auto" style={{ color: "var(--v3-text-3)" }}>
                We track 9 international + UEFA tournaments. When any of them have
                fixtures in the next 14 days, this hub auto-activates with
                standings, brackets and winner odds.
              </p>
            </div>
          </V3Section>
        ) : (
          <>
            <V3Section label="Active tournaments" testid="tournament-tabs">
              <div
                className="flex flex-wrap items-center gap-0"
                style={{ borderTop: "1px solid var(--v3-line)" }}
              >
                {active.map((t) => {
                  const activeTab = selected === t.slug;
                  return (
                    <button
                      key={t.slug}
                      onClick={() => setSelected(t.slug)}
                      data-testid={`tournament-tab-${t.slug}`}
                      className="inline-flex items-center gap-2 px-4 py-3 text-[13px] transition-colors"
                      style={{
                        borderRight: "1px solid var(--v3-line)",
                        borderBottom: "1px solid var(--v3-line)",
                        background: activeTab ? "var(--v3-text)" : "transparent",
                        color: activeTab ? "var(--v3-canvas)" : "var(--v3-text-2)",
                        fontWeight: activeTab ? 500 : 400,
                      }}
                    >
                      <span className="text-base leading-none">{t.flag}</span>
                      {t.name}
                      <span
                        className="text-[10px] uppercase"
                        style={{
                          color: activeTab ? "rgba(0,0,0,0.55)" : "var(--v3-text-3)",
                          fontFamily: "var(--v3-font-mono)",
                          letterSpacing: "0.12em",
                        }}
                      >
                        {t.fixtures_in_window}
                      </span>
                    </button>
                  );
                })}
              </div>
            </V3Section>

            {loadingDetail ? (
              <div
                className="py-10 text-[13px]"
                style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
              >
                Loading tournament data…
              </div>
            ) : !detail ? (
              <p className="text-[13px]" style={{ color: "var(--v3-text-3)" }}>
                Couldn't load tournament data.
              </p>
            ) : (
              <>
                <div
                  className="grid grid-cols-1 lg:grid-cols-3 gap-0"
                  style={{ borderTop: "1px solid var(--v3-line)" }}
                >
                  <Pane borderRight borderBottom>
                    <HeaderRow icon={Calendar} label="Upcoming + live" testid="tournament-fixtures" />
                    {(detail.live?.length || 0) + (detail.upcoming?.length || 0) === 0 ? (
                      <p className="text-[13px]" style={{ color: "var(--v3-text-3)" }}>
                        No fixtures in window.
                      </p>
                    ) : (
                      <ul className="space-y-0 max-h-[380px] overflow-auto" style={{ borderTop: "1px solid var(--v3-line)" }}>
                        {detail.live?.map((f) => (
                          <li
                            key={f.fixture_id}
                            onClick={() => navigate(`/match/${f.fixture_id}`)}
                            className="px-3 py-2.5 cursor-pointer"
                            style={{
                              borderBottom: "1px solid var(--v3-line)",
                              background: "rgba(244,63,94,0.04)",
                            }}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-[13px] leading-tight truncate" style={{ fontWeight: 500 }}>
                                {f.home_team?.name} <span style={{ color: "var(--v3-text-4)" }}>vs</span> {f.away_team?.name}
                              </div>
                              <V3Tag tone="rose">
                                <span
                                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                                  style={{ background: "var(--v3-rose, #f43f5e)" }}
                                />
                                {f.status}
                              </V3Tag>
                            </div>
                            <div
                              className="text-[11px] mt-1"
                              style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
                            >
                              {f.goals_home != null ? `${f.goals_home}–${f.goals_away}` : f.date?.slice(0, 16).replace("T", " · ")}
                            </div>
                          </li>
                        ))}
                        {detail.upcoming?.slice(0, 25).map((f) => (
                          <li
                            key={f.fixture_id}
                            onClick={() => navigate(`/match/${f.fixture_id}`)}
                            className="px-3 py-2.5 cursor-pointer"
                            style={{ borderBottom: "1px solid var(--v3-line)" }}
                          >
                            <div className="text-[13px] leading-tight truncate" style={{ fontWeight: 500 }}>
                              {f.home_team?.name} <span style={{ color: "var(--v3-text-4)" }}>vs</span> {f.away_team?.name}
                            </div>
                            <div
                              className="text-[11px] mt-1"
                              style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
                            >
                              {f.date?.slice(0, 16).replace("T", " · ")} · {f.status}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                    {detail.settled?.length > 0 && (
                      <>
                        <div className="mt-5">
                          <V3Label>Last 30 days · settled</V3Label>
                        </div>
                        <ul className="mt-3 max-h-[200px] overflow-auto" style={{ borderTop: "1px solid var(--v3-line)" }} data-testid="tournament-settled">
                          {detail.settled.slice(0, 15).map((f) => (
                            <li
                              key={f.fixture_id}
                              onClick={() => navigate(`/match/${f.fixture_id}`)}
                              className="px-3 py-2.5 cursor-pointer"
                              style={{ borderBottom: "1px solid var(--v3-line)" }}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="text-[13px] leading-tight truncate" style={{ color: "var(--v3-text-2)" }}>
                                  {f.home_team?.name} <span style={{ color: "var(--v3-text-4)" }}>vs</span> {f.away_team?.name}
                                </div>
                                <V3Num className="text-[14px] shrink-0">{f.goals_home}–{f.goals_away}</V3Num>
                              </div>
                              <div
                                className="text-[11px] mt-1"
                                style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
                              >
                                {f.date?.slice(0, 10)} · {f.status}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </Pane>

                  <Pane borderRight borderBottom>
                    <HeaderRow
                      icon={Users}
                      label="Standings"
                      testid="tournament-standings"
                      right={
                        detail.eliminated_count > 0 ? (
                          <V3Tag tone="rose">{detail.eliminated_count} out</V3Tag>
                        ) : null
                      }
                    />
                    {detail.standings.length === 0 ? (
                      <p className="text-[13px]" style={{ color: "var(--v3-text-3)" }}>
                        Not enough settled matches yet.
                      </p>
                    ) : (
                      <ul className="max-h-[480px] overflow-auto" style={{ borderTop: "1px solid var(--v3-line)" }}>
                        {detail.standings.slice(0, 16).map((s, idx) => (
                          <li
                            key={s.team_id}
                            className="flex items-center gap-2 px-3 py-2 text-[13px]"
                            style={{
                              borderBottom: "1px solid var(--v3-line)",
                              opacity: s.eliminated ? 0.55 : 1,
                              textDecoration: s.eliminated ? "line-through" : "none",
                            }}
                          >
                            <V3Num tone="var(--v3-text-3)" className="w-5 text-[11px]">
                              {String(idx + 1).padStart(2, "0")}
                            </V3Num>
                            {s.logo && <img src={s.logo} alt="" className="w-4 h-4" />}
                            <span className="flex-1 truncate">{s.team_name}</span>
                            {s.eliminated && <V3Tag tone="rose">OUT</V3Tag>}
                            <V3Num tone="var(--v3-text-3)" className="text-[11px]">
                              {s.wins}-{s.draws}-{s.losses}
                            </V3Num>
                            <V3Num className="w-8 text-right">{s.score.toFixed(0)}</V3Num>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Pane>

                  <Pane borderBottom>
                    <HeaderRow
                      icon={Trophy}
                      label="Winner odds"
                      testid="tournament-winner-odds"
                      sub="Model-implied · eliminated excluded"
                    />
                    {detail.winner_odds.length === 0 ? (
                      <p className="text-[13px]" style={{ color: "var(--v3-text-3)" }}>
                        Not enough data yet.
                      </p>
                    ) : (
                      <ul className="max-h-[460px] overflow-auto" style={{ borderTop: "1px solid var(--v3-line)" }}>
                        {detail.winner_odds.map((w, idx) => (
                          <li
                            key={w.team_id}
                            className="flex items-center gap-2 px-3 py-2 text-[13px]"
                            style={{ borderBottom: "1px solid var(--v3-line)" }}
                          >
                            <V3Num tone="var(--v3-text-3)" className="w-5 text-[11px]">
                              {String(idx + 1).padStart(2, "0")}
                            </V3Num>
                            {w.logo && <img src={w.logo} alt="" className="w-4 h-4" />}
                            <span className="flex-1 truncate">{w.team_name}</span>
                            <V3Num tone="var(--v3-text-3)" className="text-[11px]">
                              {w.implied_probability_pct}%
                            </V3Num>
                            <V3Num tone="var(--v3-indigo-fg)" className="w-14 text-right">
                              {w.fair_decimal_odds}×
                            </V3Num>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Pane>
                </div>

                {detail.bracket?.length > 0 && (
                  <V3Section label="Bracket" testid="tournament-bracket">
                    <div
                      className="overflow-x-auto p-5"
                      style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}
                    >
                      <div className="flex gap-6 min-w-fit">
                        {detail.bracket.map((round, ri) => (
                          <div key={ri} className="flex flex-col gap-3 min-w-[220px]" data-testid={`bracket-round-${ri}`}>
                            <V3Label>{round.stage}</V3Label>
                            {round.fixtures.map((f) => (
                              <div
                                key={f.fixture_id}
                                onClick={() => f.fixture_id && navigate(`/match/${f.fixture_id}`)}
                                className="px-3 py-2 cursor-pointer transition-colors"
                                style={{
                                  border: "1px solid var(--v3-line-2)",
                                  background: f.is_settled ? "var(--v3-surface)" : "rgba(99,102,241,0.04)",
                                }}
                              >
                                <div className="flex items-center justify-between gap-2 text-[13px]">
                                  <span className="truncate" style={{ fontWeight: 500 }}>
                                    {f.home_team?.name}
                                  </span>
                                  <V3Num tone="var(--v3-text-3)" className="text-[12px] shrink-0">
                                    {f.is_settled ? f.goals_home : "–"}
                                  </V3Num>
                                </div>
                                <div className="flex items-center justify-between gap-2 text-[13px]">
                                  <span className="truncate" style={{ color: "var(--v3-text-2)" }}>
                                    {f.away_team?.name}
                                  </span>
                                  <V3Num tone="var(--v3-text-3)" className="text-[12px] shrink-0">
                                    {f.is_settled ? f.goals_away : "–"}
                                  </V3Num>
                                </div>
                                <div
                                  className="text-[11px] mt-1"
                                  style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
                                >
                                  {f.date?.slice(0, 10)} · {f.status}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </V3Section>
                )}
              </>
            )}
          </>
        )}
      </V3Container>
    </V3Page>
  );
};

function Pane({ children, borderRight = false, borderBottom = false }) {
  return (
    <div
      className="p-5"
      style={{
        background: "var(--v3-surface)",
        borderRight: borderRight ? "1px solid var(--v3-line)" : "none",
        borderBottom: borderBottom ? "1px solid var(--v3-line)" : "none",
      }}
    >
      {children}
    </div>
  );
}

function HeaderRow({ icon: Icon, label, right, sub, testid }) {
  return (
    <div className="mb-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <div
          className="inline-flex items-center gap-2 text-[11px] uppercase"
          style={{
            color: "var(--v3-text-2)",
            fontFamily: "var(--v3-font-mono)",
            letterSpacing: "0.14em",
            fontWeight: 500,
          }}
        >
          <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
          {label}
        </div>
        {right}
      </div>
      {sub && (
        <p
          className="text-[11px] mt-1"
          style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

export default TournamentHubPage;
